"use client";

import {
	addToCart,
	type AddToCartActionRequest,
	type AddToCartActionResponse,
	type AddToCartActionStatus
} from "@/app/actions/cart";
import { type AddToCartSource } from "@/app/components/shared/recommended-products/add-to-cart-event";
import {
	useAddToCartErrorStore,
	useAddToCartSliderStore,
	useCartStore,
	useSessionStore
} from "@/app/store";
import { type AddedProductSnapshot } from "@/app/store/cart/add-to-cart-slider-store";
import { analyticsManager } from "@/app/utilities/analytics";
import { getAddToCartErrorMessage } from "./errorMessages";

export interface RunAddToCartArgs {
	request: AddToCartActionRequest;
	source: AddToCartSource;
	product?:
		| AddedProductSnapshot
		| (() => Promise<AddedProductSnapshot | undefined>);
}

export type RunAddToCartResult =
	{ status: "success" } | { status: AddToCartActionStatus; message: string };

export async function runAddToCart(
	args: RunAddToCartArgs
): Promise<RunAddToCartResult> {
	const sessionState = useSessionStore.getState().sessionState;

	let response: AddToCartActionResponse;
	try {
		response = await addToCart(sessionState, args.request);
	} catch {
		response = {
			status: "network-error",
			message: "Add to cart request failed"
		};
	}

	if (response.status !== "success") {
		const message = getAddToCartErrorMessage(
			response.status,
			response.message
		);
		useAddToCartSliderStore.getState().close();
		useAddToCartErrorStore.getState().fail(response.status, message);
		return { status: response.status, message };
	}

	useAddToCartErrorStore.getState().clear();
	useCartStore.getState().setCartState(response.cart);

	const product =
		typeof args.product === "function"
			? await args.product()
			: args.product;
	const quantity = Number(args.request.quantity) || 1;
	const locale =
		useSessionStore.getState().sessionState?.locale ?? sessionState?.locale;

	if (locale) {
		analyticsManager.track({
			type: "add_to_cart",
			sku: args.request.productCode,
			quantity,
			price: product?.salePrice ?? 0,
			locale,
			source: args.source
		});
	}

	if (product) {
		useAddToCartSliderStore.getState().open(product);
	}

	return { status: "success" };
}
